import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Merge conditional class names and resolve Tailwind conflicts.
 * Standard shadcn/ui helper used by every UI primitive.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/** Parse date-only values at noon so timezone conversion cannot move them to the previous day. */
export function parseAppDate(date: string | Date): Date | null {
  const value = typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date) ? `${date}T12:00:00` : date;
  const parsed = value instanceof Date ? new Date(value.getTime()) : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Format a Date as a short, locale-aware day/month/year string. */
export function formatDate(date: string | Date, locale = "en-US"): string {
  const d = parseAppDate(date);
  if (!d) return "Date not set";
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(d);
}

/** Days remaining until a target date (can be negative if past). */
export function daysUntil(date: string | Date): number {
  const target = parseAppDate(date);
  if (!target) return Number.NaN;
  const now = new Date();
  const msPerDay = 1000 * 60 * 60 * 24;
  return Math.ceil((target.setHours(0, 0, 0, 0) - now.setHours(0, 0, 0, 0)) / msPerDay);
}

/** Build initials from a display name, e.g. "Alex Rivera" -> "AR". */
export function getInitials(name: string): string {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}
